import * as React from 'react';
import {
  PresenceColorProvider,
  TokenProvider,
  ExternalContainer,
  ITelemetryNamespaceLogger,
  IComponentLastEditedTracker,
  ILastEditDetails,
  PresenceAwayStatusTracker,
  FluidUser
} from '@fluidx/office-fluid-types';
import { sendErrorEvent, sendTelemetryEvent, Locale, LocaleDirection } from '@fluidx/utilities';
import { CoauthorGalleryStyles } from '@ms/acui-collab';
import { IClient } from '@fluidframework/protocol-definitions';
import { CoauthorsUpdater, CoauthorUpdatesListener } from './LeapUpdater';
import { ErrorEvent } from '../../telemetry/ErrorEvent';
import { CoauthorGallery, CoauthorInformation } from './CoauthorGallery';
import { v4 as uuid } from 'uuid';
import { LastEdited } from './LastEdited';

export type FluidLeapStyles = { coauthorGallery: CoauthorGalleryStyles };

export interface FluidLeapProps {
  container: ExternalContainer;
  editingStatus: string;
  inactiveStatus: string;
  presenceColorProvider: PresenceColorProvider;
  pageLogger?: ITelemetryNamespaceLogger;
  tokenProvider?: TokenProvider;
  styles?: FluidLeapStyles;
  maxFacepileCoauthors?: number; // Maximum number of coins to display in the facepile
  lastEditedTracker?: IComponentLastEditedTracker;
  presenceAwayStatusTrackerPromise?: Promise<PresenceAwayStatusTracker | undefined>;

  /** Host localization */
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };
  overflowMenuHeaderText: (placeholder: { count: number }) => string;
  lastEditedTooltipText: (placeholders: { authorName: string; relativeTimestamp: string }) => string;
  lastEditedAriaLabelText: (placeholders: { relativeTimestamp: string; authorName: string }) => string;
  relativeTimestampJustNow: string;
  relativeTimestampMinutesAgo: (placeholders: { minutes: number }) => string;
  relativeTimestampMinutesAgoFull: (placeholders: { minutes: number }) => string;
  relativeTimestampHoursAgo: (placeholders: { hours: number }) => string;
  relativeTimestampHoursAgoFull: (placeholders: { hours: number }) => string;
  relativeTimestampYesterday: (placeholders: { time: string }) => string;
  relativeTimestampWithinLastDays: (placeholders: { day: string; time: string }) => string;
  relativeTimestampJustNowLowerCase: string;
  relativeTimestampYesterdayLowerCase: (placeholders: { time: string }) => string;
}

/**
 * Leap stands for Last Edited and Presence.
 *
 * This control acts as a switch between the CoauthorGallery and the LastEdited.
 * The CoauthorGallery will be displayed when there are multiple coauthors in the same document,
 * otherwise it will show the LastEdited.
 *
 * It contains the necessary logic in order to listen to the audience and to switch accordingly.
 */
export const FluidLeap = React.memo(function FluidCoauthorGallery(props: FluidLeapProps) {
  const {
    container,
    overflowMenuHeaderText,
    editingStatus,
    presenceColorProvider,
    pageLogger,
    tokenProvider,
    styles,
    maxFacepileCoauthors,
    lastEditedTracker,
    localization,
    lastEditedTooltipText,
    lastEditedAriaLabelText,
    relativeTimestampJustNow,
    relativeTimestampMinutesAgo,
    relativeTimestampMinutesAgoFull,
    relativeTimestampHoursAgo,
    relativeTimestampHoursAgoFull,
    relativeTimestampYesterday,
    relativeTimestampWithinLastDays,
    relativeTimestampJustNowLowerCase,
    relativeTimestampYesterdayLowerCase,
    presenceAwayStatusTrackerPromise,
    inactiveStatus
  } = props;

  const galleryId = React.useRef<string>(uuid());
  const [coauthors, setCoauthors] = React.useState<CoauthorInformation[]>([]);
  const [lastEditDetails, setLastEditDetails] = React.useState<ILastEditDetails>();
  const [presenceAwayStatusTracker, setPresenceAwayStatusTracker] = React.useState<
    PresenceAwayStatusTracker | undefined
  >(undefined);

  // Keeps track of the mounted state of the control.
  const isMounted = React.useRef(false);
  // This effect's purpose is to keep track of the mounted state of the control.
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const coauthorUpdatesListener = React.useCallback<CoauthorUpdatesListener>((coauthors: CoauthorInformation[]) => {
    if (!isMounted.current) {
      return;
    }

    // The persona card that pops up when you hover over a coauthor is placed depending ont the id of the coauthor.
    // To ensure they always appear correctly when there is more than one coauthor gallery on a page we need to make sure it
    // is unique per gallery.
    coauthors.forEach((coauthor) => {
      const userId = coauthor.id;
      coauthor.id = galleryId.current + userId;
    });
    setCoauthors(coauthors);
  }, []);

  const coauthorsUpdater = React.useRef<CoauthorsUpdater>(
    new CoauthorsUpdater(presenceColorProvider, coauthorUpdatesListener, pageLogger, tokenProvider)
  );

  React.useEffect(() => {
    // If the token provider is not defined, log for debugging purposes
    if (pageLogger && !tokenProvider) {
      sendTelemetryEvent(pageLogger, { eventName: 'MissingTokenProvider' });
    }
  }, []);

  React.useEffect(() => {
    coauthorsUpdater.current.setPresenceColorProvider(presenceColorProvider);
  }, [presenceColorProvider]);

  const getFluidUser = (client: IClient, clientId: string): FluidUser => {
    const user = client.user as FluidUser;
    return {
      // user.id represents the UPN of the coauthor, which also helps to assign a color. In case, there is no UPN set, use the client Id
      id: user.id ? user.id : clientId,
      name: user.name
    };
  };

  const getCurrentUserId = (): string | undefined => {
    if (container.clientId !== undefined) {
      const currentClient = container.audience.getMembers().get(container.clientId);
      if (currentClient) {
        coauthorsUpdater.current.setCurrentAuthor(currentClient.user.id);
        return currentClient.user.id;
      }
    }
    return undefined;
  };

  const computeCoauthors = () => {
    // Make sure the container is connected and has a proper client id, otherwise, we'll wait until the container connects.
    if (!container.connected || !container.clientId) {
      return;
    }

    // We should start computing coauthors only if the current user is the audience, otherwise he might appear in the facepile.
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      return;
    }

    const coauthorsArray: Omit<CoauthorInformation, 'color'>[] = [];
    const audienceMembers = container.audience.getMembers();

    audienceMembers.forEach((client, clientId) => {
      if (!clientId || !client || !client.user) {
        pageLogger &&
          sendErrorEvent(pageLogger, {
            eventName: ErrorEvent.InvalidClientInfo,
            message: 'clientId and/or sequencedClient information cannot be undefined for an audienceMember'
          });

        return;
      }

      const user: FluidUser = getFluidUser(client, clientId);
      if (user.id === currentUserId) {
        return;
      }

      const isUserActive = !presenceAwayStatusTracker || !presenceAwayStatusTracker.isUserAway(user.id);
      coauthorsArray.push({
        id: user.id,
        description: isUserActive ? editingStatus : inactiveStatus,
        name: user.name,
        activeStatus: isUserActive,
        hasRing: isUserActive
      });
    });

    // tslint:disable-next-line: no-floating-promises
    coauthorsUpdater.current.updateCoauthors(coauthorsArray);
  };

  React.useEffect(() => {
    const addAudienceListeners = () => {
      container.audience.on('addMember', computeCoauthors);
      container.audience.on('removeMember', computeCoauthors);
    };

    const setup = () => {
      addAudienceListeners();
      // This will make sure that we are displaying the current coauthors from the audience, if any.
      computeCoauthors();
      // As soon as the setup ends, we should remove the 'connected' listener since we want to be executed only once.
      container.removeListener('connected', setup);
    };

    if (container.connected) {
      setup();
    } else {
      container.on('connected', setup);
    }

    return () => {
      // tslint:disable-next-line: no-floating-promises
      coauthorsUpdater.current.updateCoauthors([]).then(() => {
        container.audience.removeListener('addMember', computeCoauthors);
        container.audience.removeListener('removeMember', computeCoauthors);
      });
    };
  }, [container]);

  React.useEffect(() => {
    if (!lastEditedTracker) {
      return;
    }

    const fetchLastEditDetails = (): void => {
      const storedLastEditDetails = lastEditedTracker.getLastEditDetails();
      if (storedLastEditDetails) {
        // Ignore same state.
        if (
          !lastEditDetails ||
          lastEditDetails.user.id !== storedLastEditDetails.user.id ||
          lastEditDetails.timestamp !== storedLastEditDetails.timestamp
        ) {
          setLastEditDetails(storedLastEditDetails);
        }
      }
    };

    // The LastEditedTracker is also a Partial<EventEmitter>, so the props are optional
    // and we need check beforehand if the 'on' & 'off' listeners are defined.
    if (lastEditedTracker.on) {
      // This event is for every last edit change.
      lastEditedTracker.on('lastEditChanged', fetchLastEditDetails);
      // This event is in case the last edited component loaded after the current component loaded
      // so make sure we fetch the initial value when it eventually loaded.
      lastEditedTracker.on('lastEditedComponentLoaded', fetchLastEditDetails);
    }

    // Fetch initial value.
    fetchLastEditDetails();

    return () => {
      // Remove the associated listener for the 'lastEditChanged' event.
      if (lastEditedTracker.off) {
        lastEditedTracker.off('lastEditChanged', fetchLastEditDetails);
        lastEditedTracker.off('lastEditedComponentLoaded', fetchLastEditDetails);
      }
    };
  }, [lastEditedTracker]);

  React.useEffect(() => {
    if (!presenceAwayStatusTrackerPromise) {
      return;
    }

    const getAwayStatusTracker = async () => {
      const awayStatusTracker = await presenceAwayStatusTrackerPromise;
      setPresenceAwayStatusTracker(awayStatusTracker);
    };

    // tslint:disable-next-line: no-floating-promises
    getAwayStatusTracker();
  }, [presenceAwayStatusTrackerPromise]);

  React.useEffect(() => {
    if (!presenceAwayStatusTracker) {
      return;
    }

    presenceAwayStatusTracker.on('userAwayStatusChanged', computeCoauthors);
    computeCoauthors();

    return () => {
      if (presenceAwayStatusTracker.off) {
        presenceAwayStatusTracker.off('userAwayStatusChanged', computeCoauthors);
      }
    };
  }, [presenceAwayStatusTracker]);

  return coauthors.length === 0 && lastEditDetails ? (
    <LastEdited
      lastEditDetails={lastEditDetails}
      presenceColorProvider={presenceColorProvider}
      pageLogger={pageLogger}
      tokenProvider={tokenProvider}
      localization={localization}
      lastEditedTooltipText={lastEditedTooltipText}
      lastEditedAriaLabelText={lastEditedAriaLabelText}
      relativeTimestampJustNow={relativeTimestampJustNow}
      relativeTimestampMinutesAgo={relativeTimestampMinutesAgo}
      relativeTimestampMinutesAgoFull={relativeTimestampMinutesAgoFull}
      relativeTimestampHoursAgo={relativeTimestampHoursAgo}
      relativeTimestampHoursAgoFull={relativeTimestampHoursAgoFull}
      relativeTimestampYesterday={relativeTimestampYesterday}
      relativeTimestampWithinLastDays={relativeTimestampWithinLastDays}
      relativeTimestampJustNowLowerCase={relativeTimestampJustNowLowerCase}
      relativeTimestampYesterdayLowerCase={relativeTimestampYesterdayLowerCase}
    />
  ) : (
    <CoauthorGallery
      coauthors={coauthors}
      overflowMenuHeaderTextString={overflowMenuHeaderText}
      styles={styles?.coauthorGallery}
      maxFacepileCoauthors={maxFacepileCoauthors}
    />
  );
});

// cSpell:ignore facepile
